import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const HERO_IMG = "/images/hero-crew.jpg";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden py-1">
    <motion.div
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Commercial cleaning" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0C1B24]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C1B24]/85 via-[#0C1B24]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C1B24] via-transparent to-[#0C1B24]/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-40 pb-16 md:pb-24">
        <MaskedLine delay={0.2}>
          <p
            data-testid="hero-overline"
            className="font-spacemono text-xs md:text-sm tracking-[0.35em] text-[#00F0FF] uppercase"
          >
            [ Commercial &amp; Corporate Cleaning — Muzaffarnagar, UP ]
          </p>
        </MaskedLine>

        <h1 className="mt-6 font-khand font-bold leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.35}>
            <span data-testid="hero-headline-1" className="block">उत्तर भारत की अग्रणी</span>
          </MaskedLine>
          <MaskedLine delay={0.5}>
            <span data-testid="hero-headline-2" className="block">
              कमर्शियल <span className="text-[#00F0FF]">क्लीनिंग</span> कंपनी
            </span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.7} className="max-w-2xl">
          <p data-testid="hero-subheadline" className="mt-6 text-base md:text-lg text-slate-300 font-mukta leading-relaxed">
            आधुनिक तकनीक, पर्यावरण-अनुकूल रसायन और प्रशिक्षित कार्यबल — कॉर्पोरेट ऑफिस, आईटी पार्क,
            हॉस्पिटल और सरकारी विभागों के लिए स्वच्छ व कीटाणुरहित वातावरण।
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-demo-btn"
            onClick={() => scrollToSection("#booking")}
            className="group bg-[#00F0FF] text-[#0C1B24] font-mukta font-semibold text-base px-8 py-4 flex items-center gap-3 hover:bg-white transition-colors shadow-[0_0_36px_rgba(0,240,255,0.35)] hover:shadow-[0_0_52px_rgba(0,240,255,0.55)]"
          >
            अभी Enquiry करें
            <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-1" />
          </button>
          <a
            data-testid="hero-whatsapp-btn"
            href="https://wa.me/918923917079"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white/25 text-white font-mukta font-medium px-8 py-4 flex items-center gap-3 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp करें
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          data-testid="hero-tagline"
          className="mt-10 font-spacemono text-[11px] tracking-[0.3em] text-slate-500 uppercase"
        >
          जल्दी बुक करें — विशेष ऑफर उपलब्ध · 100% Police Verified स्टाफ · SLA गारंटी
        </motion.p>
      </motion.div>
    </section>
  );
};
